import React, { useCallback, useState } from "react";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Button, Badge, type BadgeVariant } from "../../components/ui/index.ts";
import TimeEntryTab from "../../components/features/timesheet/tabs/time-entry-tab/time-entry-tab.tsx";
import { ChartColumn, FileSpreadsheet, RefreshCw } from "lucide-react";
import { useTimesheetEntries } from "../../components/features/timesheet/hooks/use-timesheet-entries.ts";
import { useRequestTimesheetExtension } from "../../components/features/timesheet/hooks/use-request-timesheet-extension.ts";
import { ExtendTimesheetModal } from "../../components/features/timesheet/components/extend-timesheet-modal/extend-timesheet-modal.tsx";
import {
  exceedsProjectRemainingBal,
  getAllocationForProject,
  minutesUsedForProjectInEntries,
} from "../../components/features/timesheet/utils/timesheet-allocation.ts";
import { shouldShowAttemptsRemainingBadge } from "../../components/features/timesheet/utils/timesheet-extension-copy.ts";
import type { TimesheetEntry } from "../../components/features/timesheet/types.ts";
import { TailSpin } from "react-loader-spinner";
import { showError } from "../../utils/toast.ts";
import styles from "./timesheet.module.css";
import Guard from "../../lib/rbac/guard.tsx";
import { getTimesheetLockReason } from "../../components/features/timesheet/utils/timesheet-allowed-dates.ts";
import DownloadReportModal from "../../components/features/timesheet/modals/download-report-modal/download-report-modal.tsx";
import { useUser } from "../../lib/rbac/useUser.ts";
import { useOpenTimesheetEmployee } from "../../components/features/timesheet/hooks/use-open-timesheet-employee.ts";
import { isWithinTimesheetOpenWindow } from "../../components/features/timesheet/utils/timesheet-open-window.ts";

function formatTimesheetTypeLabel(type: string): string {
  const normalized = type.trim().toUpperCase();
  if (normalized === "WFH") return "Work from Home";
  if (normalized === "REGULAR") return "Regular";
  if (normalized === "REMOTE") return "Remote";
  if (normalized === "CONSULTANT") return "Consultant";
  return type
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function attemptsBadgeVariant(attempts: number): BadgeVariant {
  if (attempts <= 0) return "danger";
  if (attempts <= 2) return "warning";
  return "success";
}

function typeBadgeVariant(type: string): BadgeVariant {
  return type.trim().toUpperCase() === "WFH" ? "warning" : "neutral";
}

function isTruthyRemoteFlag(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value !== "string") return false;
  return value.trim().toLowerCase() === "true";
}

function getTimesheetLockMessage(lockReason: ReturnType<typeof getTimesheetLockReason>): string {
  if (lockReason === "TODAY_BEFORE_NOON") {
    return "The timesheet submission window will open at 12:00 PM.";
  }
  return "Timesheet window is closed.";
}

/*
 * TEMPORARY TIMESHEET WINDOW — remove this function and its single usage below
 * after the 25, 26 and 31 August 2026 company-wide timesheet reopening is no longer needed.
 * The window remains open through 2 September and closes at local midnight.
 */
function isWithinTemporaryAugustTimesheetWindow(selectedDate: Date, now = new Date()): boolean {
  const workDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
  const openWorkDates = new Set([
    new Date(2026, 7, 25).getTime(),
    new Date(2026, 7, 26).getTime(),
    new Date(2026, 7, 31).getTime(),
  ]);
  const windowClosesAt = new Date(2026, 8, 3, 0, 0, 0, 0);

  return now < windowClosesAt && openWorkDates.has(workDate.getTime());
}

// ─── Page ────────────────────────────────────────────────
const Timesheet = () => {
  const user = useUser();
  const isOpenTimesheetEmployee = useOpenTimesheetEmployee(user?.id);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isExtendOpen, setIsExtendOpen] = useState(false);
  const { requestExtension, isRequesting } = useRequestTimesheetExtension();
  const {
    entries,
    setEntries,
    draftId,
    setDraftId,
    isSubmitted,
    setIsSubmitted,
    statusInfo,
    sheetMeta,
    loading: isInitialLoading,
    isRefreshing,
    refetch,
  } = useTimesheetEntries(selectedDate);

  const userRecord = user as typeof user & { isremote?: boolean | string };
  const isRemoteEmployee =
    isTruthyRemoteFlag(user?.isRemote) ||
    isTruthyRemoteFlag(userRecord?.isremote) ||
    user?.empType?.trim().toUpperCase() === "REMOTE" ||
    user?.subType?.trim().toUpperCase() === "REMOTE" ||
    sheetMeta?.type?.trim().toUpperCase() === "REMOTE";
  const editableWindowDays = isRemoteEmployee ? 3 : 2;
  const defaultLockReason = getTimesheetLockReason(selectedDate, {
    allowedTill: sheetMeta?.allowedTill ?? statusInfo?.allowedTill,
    timesheetFound: sheetMeta?.timesheetFound,
    editableWindowDays,
  });
  const hasTemporaryAugustWindow = isWithinTemporaryAugustTimesheetWindow(selectedDate);
  const hasTemporaryOpenWindow =
    hasTemporaryAugustWindow ||
    (isOpenTimesheetEmployee && isWithinTimesheetOpenWindow(selectedDate));
  const lockReason = hasTemporaryOpenWindow ? null : defaultLockReason;
  console.log("[Timesheet window check]", {
    currentLocalTime: new Date().toString(),
    currentIsoTime: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    selectedDate: selectedDate.toLocaleDateString("en-CA"),
    editableWindowDays,
    isRemoteEmployee,
    userIsRemote: user?.isRemote,
    userIsremote: userRecord?.isremote,
    empType: user?.empType,
    subType: user?.subType,
    sheetType: sheetMeta?.type,
    timesheetFound: sheetMeta?.timesheetFound,
    allowedTill: sheetMeta?.allowedTill ?? statusInfo?.allowedTill,
    lockReason,
  });
  const historicalViewOnly = lockReason != null;
  const timesheetLockMessage = getTimesheetLockMessage(lockReason);
  const effectiveStatusInfo =
    hasTemporaryOpenWindow && statusInfo
      ? { ...statusInfo, expired: false }
      : statusInfo;

  const handleAddEntry = (entry: TimesheetEntry) => {
    if (historicalViewOnly) {
      showError(timesheetLockMessage);
      return;
    }

    const currentTotalMinutes = entries.reduce(
      (acc, e) => acc + (e.hours * 60 + (Number(e.minutes) || 0)),
      0,
    );
    const newEntryMinutes = entry.hours * 60 + (Number(entry.minutes) || 0);

    if (currentTotalMinutes + newEntryMinutes > 40 * 60) {
      showError("Total daily time cannot exceed 40 hours.");
      return;
    }

    const allocations = statusInfo?.projectAllocations;
    if (
      entry.titleType === "PROJECT" &&
      entry.projectId != null &&
      exceedsProjectRemainingBal({
        projectId: entry.projectId,
        titleType: entry.titleType,
        entryMinutes: newEntryMinutes,
        otherEntriesMinutesForSameProject: minutesUsedForProjectInEntries(
          entries,
          entry.projectId,
        ),
        remainingBal: getAllocationForProject(allocations, entry.projectId)
          ?.remainingBal,
      })
    ) {
      showError("Time exceeds remaining allocation for this project.");
      return;
    }

    setEntries((prev) => [...prev, entry]);
  };

  const handleDeleteEntry = (id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const handleEditEntry = (updated: TimesheetEntry) => {
    if (historicalViewOnly) {
      showError(timesheetLockMessage);
      return;
    }

    const otherEntries = entries.filter((e) => e.id !== updated.id);
    const totalMinutesOthers = otherEntries.reduce(
      (acc, e) => acc + (e.hours * 60 + (Number(e.minutes) || 0)),
      0,
    );
    const updatedEntryMinutes = updated.hours * 60 + (Number(updated.minutes) || 0);

    if (totalMinutesOthers + updatedEntryMinutes > 40 * 60) {
      showError("Total daily time cannot exceed 40 hours.");
      return;
    }

    const allocations = statusInfo?.projectAllocations;
    if (
      updated.titleType === "PROJECT" &&
      updated.projectId != null &&
      exceedsProjectRemainingBal({
        projectId: updated.projectId,
        titleType: updated.titleType,
        entryMinutes: updatedEntryMinutes,
        otherEntriesMinutesForSameProject: minutesUsedForProjectInEntries(
          entries,
          updated.projectId,
          updated.id,
        ),
        remainingBal: getAllocationForProject(allocations, updated.projectId)
          ?.remainingBal,
      })
    ) {
      showError("Time exceeds remaining allocation for this project.");
      return;
    }

    setEntries((prev) =>
      prev.map((e) =>
        e.id === updated.id
          ? { ...e, ...updated, isModified: true }
          : e
      )
    );
  };

  const getOtherEntriesMinutesForProject = useCallback(
    (projectId: number) =>
      minutesUsedForProjectInEntries(entries, projectId),
    [entries],
  );

  const extensionHrStatus = sheetMeta?.hrStatus ?? null;
  const normalizedExtensionHrStatus = (extensionHrStatus ?? "")
    .trim()
    .toUpperCase();
  const isExtensionRequestPending = normalizedExtensionHrStatus === "PENDING";

  // Show extend button when:
  // - there is no timesheet found for the selected date (timesheetFound === false),
  // - OR the server returned a draft (isSubmitted === 0 -> isSubmitted === false and draftId exists),
  // - OR the timesheet status indicates the timesheet is expired (statusInfo?.expired)
  // AND lockReason is view-only and there is no pending extension request.
  const showExtendButton =
    !hasTemporaryOpenWindow &&
    sheetMeta != null &&
    (sheetMeta.timesheetFound === false ||
      (isSubmitted === false && draftId != null) ||
      (statusInfo?.expired === true)) &&
    lockReason === "VIEW_ONLY" &&
    !isExtensionRequestPending;

  const handleConfirmExtension = async () => {
    if (!sheetMeta) return;
    const workDate = selectedDate.toLocaleDateString("en-CA");
    const ok = await requestExtension(workDate, sheetMeta.employeeId);
    if (ok) {
      setIsExtendOpen(false);
      refetch();
    }
  };

  return (
    <PageShell>
      <div className={styles.pageContent}>
      {isInitialLoading && (
        <div className={styles.overlay} role="status" aria-live="polite" aria-label="Loading timesheet">
          <TailSpin
            height="80"
            width="80"
            color="var(--primary-color)"
            visible={true}
          />
        </div>
      )}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          {sheetMeta ? (
            <div className={styles.badgeGroup}>
              {shouldShowAttemptsRemainingBadge(sheetMeta.type) ? (
                <Badge
                  variant={attemptsBadgeVariant(sheetMeta.attemptsRemaining)}
                  className={styles.iconBadge}
                >
                  <RefreshCw size={14} strokeWidth={2.25} aria-hidden />
                  Attempts Remaining: {sheetMeta.attemptsRemaining}
                </Badge>
              ) : null}
              <Badge
                variant={typeBadgeVariant(sheetMeta.type)}
                className={styles.iconBadge}
              >
                <FileSpreadsheet size={14} strokeWidth={2.25} aria-hidden />
                {formatTimesheetTypeLabel(sheetMeta.type)}
              </Badge>
            </div>
          ) : null}
        </div>
        <div className={styles.toolbarActions}>
          <Guard permission="timesheet-download:access">
            <Button variant="muted-outline" onClick={() => setIsDownloadOpen(true)}>
              <ChartColumn className="icon-sm" />
              Download Reports
            </Button>
          </Guard>
        </div>
      </div>
      {sheetMeta && showExtendButton ? (
        <ExtendTimesheetModal
          open={isExtendOpen}
          meta={sheetMeta}
          workDate={selectedDate}
          onClose={() => setIsExtendOpen(false)}
          onConfirm={handleConfirmExtension}
          isSubmitting={isRequesting}
        />
      ) : null}
      <Guard permission="timesheet-download:access">
        <DownloadReportModal
          open={isDownloadOpen}
          onClose={() => setIsDownloadOpen(false)}
        />
      </Guard>

      <TimeEntryTab
        entries={entries}
        setEntries={setEntries}
        handleAddEntry={handleAddEntry}
        handleDeleteEntry={handleDeleteEntry}
        handleEditEntry={handleEditEntry}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        draftId={draftId}
        setDraftId={setDraftId}
        isSubmitted={isSubmitted}
        setIsSubmitted={setIsSubmitted}
        statusInfo={effectiveStatusInfo ?? undefined}
        projectAllocations={statusInfo?.projectAllocations}
        getOtherEntriesMinutesForProject={getOtherEntriesMinutesForProject}
        showExtendButton={showExtendButton}
        extensionHrStatus={hasTemporaryOpenWindow ? null : extensionHrStatus}
        onExtendClick={() => setIsExtendOpen(true)}
        isExtendLoading={isInitialLoading || isRefreshing || isRequesting}
        isRefreshing={isRefreshing}
        onRefetch={refetch}
        historicalViewOnly={historicalViewOnly}
        timesheetLockMessage={timesheetLockMessage}
      />
      </div>
    </PageShell>
  );
};

export default Timesheet;
